import React from 'react';
import { Lightbulb, Activity, Zap, AlertCircle, Info, CheckCircle, XCircle, HelpCircle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета освещения
 */
const ResultsOverview = ({ results, formData }) => {
  if (!results) return null;

  // Определение статуса соответствия нормам
  const getComplianceStatus = () => {
    switch (results.standardCompliance) {
      case 'below':
        return {
          icon: <XCircle className="w-5 h-5 text-red-500" />,
          text: 'Ниже минимальных норм',
          description: `Расчетная освещенность ${formatNumber(results.actualIlluminance, 0)} лк ниже минимальной нормы ${results.minRequired} лк`,
          color: 'red'
        };
      case 'minimal':
        return {
          icon: <AlertCircle className="w-5 h-5 text-yellow-500" />,
          text: 'Минимальное соответствие',
          description: `Освещенность соответствует минимальным нормам, но ниже рекомендуемой (${results.recommended} лк)`,
          color: 'yellow'
        };
      case 'optimal':
        return {
          icon: <CheckCircle className="w-5 h-5 text-green-500" />,
          text: 'Оптимальное соответствие',
          description: `Освещенность оптимальна и соответствует нормам СП 52.13330.2016`,
          color: 'green'
        };
      case 'excessive':
        return {
          icon: <AlertCircle className="w-5 h-5 text-blue-500" />,
          text: 'Избыточное освещение',
          description: 'Освещенность превышает рекомендуемые значения, возможен перерасход энергии',
          color: 'blue'
        };
      default:
        return {
          icon: <HelpCircle className="w-5 h-5 text-gray-500" />,
          text: 'Статус не определен',
          description: 'Не удалось сопоставить с нормативами для данного типа помещения',
          color: 'gray'
        };
    }
  };

  // Определение энергоэффективности
  const getEnergyEfficiency = () => {
    switch (results.energyEfficiency) {
      case 'excellent':
        return {
          text: 'Отличная',
          description: 'Очень низкое энергопотребление, соответствует высшим классам энергоэффективности',
          color: 'green'
        };
      case 'good':
        return {
          text: 'Хорошая',
          description: 'Низкое энергопотребление, энергоэффективное освещение',
          color: 'green'
        };
      case 'average':
        return {
          text: 'Средняя',
          description: 'Стандартное энергопотребление для данного типа помещения',
          color: 'yellow'
        };
      case 'poor':
        return {
          text: 'Низкая',
          description: 'Повышенное энергопотребление, рекомендуется оптимизация',
          color: 'orange'
        };
      case 'very-poor':
        return {
          text: 'Очень низкая',
          description: 'Высокое энергопотребление, необходима модернизация системы освещения',
          color: 'red'
        };
      default:
        return {
          text: 'Не определена',
          description: 'Не удалось определить энергоэффективность',
          color: 'gray'
        };
    }
  };

  const complianceStatus = getComplianceStatus();
  const energyEfficiency = getEnergyEfficiency();

  return (
    <ToolResult title="Результаты расчета освещения">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Количество светильников */}
        <div className="rounded-lg bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 p-5 border border-amber-100 dark:border-amber-800/50 shadow-sm">
          <h3 className="text-base font-medium text-amber-700 dark:text-amber-300 mb-1.5 flex items-center gap-1.5">
            <Lightbulb className="w-4 h-4"/>
            Количество светильников
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-amber-800 dark:text-amber-200" aria-live="polite">
              {results.recommendedLampCount} шт.
            </span>
            <span className="text-sm text-amber-600 dark:text-amber-400">
              ({results.lampsInRow} × {results.lampsInColumn})
            </span>
          </div>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
            Рекомендуемое количество светильников для данного помещения
          </p>
        </div>

        {/* Фактическая освещенность */}
        <div className={`rounded-lg bg-gradient-to-br from-${complianceStatus.color}-50 to-${complianceStatus.color}-50/70 dark:from-${complianceStatus.color}-900/30 dark:to-${complianceStatus.color}-900/20 p-5 border border-${complianceStatus.color}-100 dark:border-${complianceStatus.color}-800/50 shadow-sm`}>
          <h3 className={`text-base font-medium text-${complianceStatus.color}-700 dark:text-${complianceStatus.color}-300 mb-1.5 flex items-center gap-1.5`}>
            <Activity className="w-4 h-4"/>
            Освещенность помещения
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className={`text-3xl font-bold text-${complianceStatus.color}-800 dark:text-${complianceStatus.color}-200`} aria-live="polite">
              {formatNumber(results.actualIlluminance, 0)} лк
            </span>
          </div>
          <p className={`text-xs text-${complianceStatus.color}-600 dark:text-${complianceStatus.color}-400 mt-1 flex items-center gap-1`}>
            {complianceStatus.icon} {complianceStatus.text}
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Размеры помещения" 
          value={`${formatNumber(parseFloat(formData.roomLength), 1)} × ${formatNumber(parseFloat(formData.roomWidth), 1)} × ${formatNumber(parseFloat(formData.roomHeight), 1)} м`} 
          color="gray" 
        />
        
        <ResultItem 
          label="Площадь помещения" 
          value={`${formatNumber(results.roomArea, 1)} м²`} 
          color="gray" 
        />
        
        <ResultItem 
          label="Удельная мощность освещения" 
          value={`${formatNumber(results.specificPower, 1)} Вт/м²`} 
          color={energyEfficiency.color} 
          icon={<Zap className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Общая мощность освещения" 
          value={`${formatNumber(results.totalPower, 0)} Вт`} 
          color="amber" 
        />
        
        <ResultItem 
          label="Расположение светильников" 
          value={`${results.lampsInRow} ряд(ов) по ${results.lampsInColumn} светильник(ов)`} 
        />
        
        <ResultItem 
          label="Расстояние между светильниками" 
          value={`По длине: ${formatNumber(results.adjustedLampDistanceX, 2)} м, По ширине: ${formatNumber(results.adjustedLampDistanceY, 2)} м`} 
        />
        
        <ResultItem 
          label="Индекс помещения" 
          value={formatNumber(results.roomIndex, 2)} 
        />
        
        <ResultItem 
          label="Коэффициент использования" 
          value={formatNumber(results.utilizationFactor, 2)} 
        />
        
        <ResultItem 
          label="Тип источника света" 
          value={formData.lampType === 'led' ? 'Светодиодный (LED)' : 
                formData.lampType === 'fluorescent' ? 'Люминесцентный' :
                formData.lampType === 'halogen' ? 'Галогенный' :
                formData.lampType === 'incandescent' ? 'Лампа накаливания' :
                formData.lampType === 'metalHalide' ? 'Металлогалогенный' : 'Пользовательский'}
          color="amber" 
        />
        
        <ResultItem 
          label="Энергоэффективность" 
          value={energyEfficiency.text} 
          color={energyEfficiency.color} 
        />

        {/* Информация о соответствии нормам */}
        <div className={`bg-${complianceStatus.color}-50 dark:bg-${complianceStatus.color}-900/30 rounded-lg p-4 mt-4 border border-${complianceStatus.color}-200 dark:border-${complianceStatus.color}-700`}>
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              {complianceStatus.icon}
            </div>
            <div>
              <h4 className={`text-sm font-medium text-${complianceStatus.color}-700 dark:text-${complianceStatus.color}-300 mb-1`}>Соответствие нормам освещенности</h4>
              <p className={`text-sm text-${complianceStatus.color}-600 dark:text-${complianceStatus.color}-400`}>
                {complianceStatus.description}
              </p>
              {results.standardCompliance !== 'unknown' && (
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                  Согласно СП 52.13330.2016 и СанПиН 1.2.3685-21, для данного типа помещения: 
                  мин. норма – {results.minRequired} лк, рекомендуемая – {results.recommended} лк
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Информация об энергоэффективности */}
        <div className={`bg-${energyEfficiency.color}-50 dark:bg-${energyEfficiency.color}-900/30 rounded-lg p-4 mt-2 border border-${energyEfficiency.color}-200 dark:border-${energyEfficiency.color}-700`}>
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <Zap className={`w-5 h-5 text-${energyEfficiency.color}-500 dark:text-${energyEfficiency.color}-400`} />
            </div>
            <div>
              <h4 className={`text-sm font-medium text-${energyEfficiency.color}-700 dark:text-${energyEfficiency.color}-300 mb-1`}>Энергетическая эффективность системы освещения</h4>
              <p className={`text-sm text-${energyEfficiency.color}-600 dark:text-${energyEfficiency.color}-400`}>
                {energyEfficiency.description}
              </p>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                Удельная мощность: {formatNumber(results.specificPower, 1)} Вт/м². 
                Эффективность освещения: {formatNumber(results.illuminancePerWatt, 1)} лк·м²/Вт.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Рекомендации */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
        <h3 className="text-md font-medium text-blue-700 dark:text-blue-300 mb-2 flex items-center">
          <Info className="w-5 h-5 mr-2" />
          Рекомендации по улучшению освещения
        </h3>
        <ul className="space-y-2 text-sm text-blue-600 dark:text-blue-400">
          {results.standardCompliance === 'below' && (
            <li className="flex items-start gap-2">
              <div className="w-4 h-4 rounded-full bg-red-500 mt-1 flex-shrink-0"></div>
              <div>Увеличьте количество светильников до {Math.ceil(results.recommendedLampCount * 1.2)} шт. для достижения минимальной нормы освещенности</div>
            </li>
          )}
          {results.standardCompliance === 'minimal' && (
            <li className="flex items-start gap-2">
              <div className="w-4 h-4 rounded-full bg-yellow-500 mt-1 flex-shrink-0"></div>
              <div>Для комфортного освещения рекомендуется увеличить количество светильников до {Math.ceil(results.recommendedLampCount * 1.1)} шт.</div>
            </li>
          )}
          {results.standardCompliance === 'excessive' && (
            <li className="flex items-start gap-2">
              <div className="w-4 h-4 rounded-full bg-blue-500 mt-1 flex-shrink-0"></div>
              <div>Возможно уменьшение количества светильников до {Math.floor(results.recommendedLampCount * 0.8)} шт. для экономии энергии</div>
            </li>
          )}
          {results.energyEfficiency === 'poor' || results.energyEfficiency === 'very-poor' ? (
            <li className="flex items-start gap-2">
              <div className="w-4 h-4 rounded-full bg-orange-500 mt-1 flex-shrink-0"></div>
              <div>Рекомендуется заменить текущие источники света на более энергоэффективные (LED с эффективностью 100-120 лм/Вт)</div>
            </li>
          ) : (
            <li className="flex items-start gap-2">
              <div className="w-4 h-4 rounded-full bg-green-500 mt-1 flex-shrink-0"></div>
              <div>Используемые источники света имеют хорошую энергетическую эффективность</div>
            </li>
          )}
          <li className="flex items-start gap-2">
            <div className="w-4 h-4 rounded-full bg-amber-500 mt-1 flex-shrink-0"></div>
            <div>Оптимальное расстояние между светильниками: по длине - {formatNumber(results.adjustedLampDistanceX, 2)} м, по ширине - {formatNumber(results.adjustedLampDistanceY, 2)} м</div>
          </li>
          <li className="flex items-start gap-2">
            <div className="w-4 h-4 rounded-full bg-purple-500 mt-1 flex-shrink-0"></div>
            <div>Для данного типа помещения рекомендуется цветовая температура {formData.roomType === 'livingRoom' || formData.roomType === 'bedroom' ? '2700-3000K (теплый свет)' : formData.roomType === 'kitchen' || formData.roomType === 'bathroom' ? '4000K (нейтральный свет)' : '4000-5000K (нейтральный/холодный свет)'}</div>
          </li>
        </ul>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;